import React, {useMemo, useState} from "react";

import cx from "classnames";
import {FreeMode, Keyboard, Navigation, Pagination, Thumbs} from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {Swiper, SwiperSlide} from "swiper/react";
import {Swiper as SwiperType} from "swiper/types";

import {EventOrigin, recordEvent} from "@globals/config/eventUtils";
import {getResolutionImgUrl} from "@globals/functions/getResolutionImgUrl";
import {ImageSizeGroup} from "@globals/functions/imageUtils";
import {MediaItem, PhotoMediaItem} from "@globals/types/PhotoMediaItem";

import HideText from "@components/HideText";
import TransparentButton from "@components/TransparentButton/TransparentButton";

import styles from "./MediaAlbumCarousel.scss";

interface MediaAlbumCarousel {
  // making key required to force re-render carousel after slides data changed
  // otherwise Swiper will crashed with stale state
  key: string | number;
  keyPhoto?: PhotoMediaItem;
  mediaAlbum?: {
    id: number;
    mediae: Array<MediaItem>;
  };
  imageSizeGroup?: ImageSizeGroup;
  size?: string;
  sizesAttribute?: string;
  showThumbnails?: boolean;
  wrapperClassName?: string;
  eventOrigin?: EventOrigin;
  itemId?: number;
}

export default function MediaAlbumCarousel({
  showThumbnails = true,
  wrapperClassName,
  eventOrigin = EventOrigin.PAGE,
  ...props
}: MediaAlbumCarousel): JSX.Element {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType>();

  const mediaItems = useMemo<PhotoMediaItem[]>(() => {
    let updatedMediaItems: Array<PhotoMediaItem> = [];

    if (props.keyPhoto) {
      updatedMediaItems.push(props.keyPhoto);
    }

    if (props?.mediaAlbum?.mediae) {
      const mediaItems: Array<PhotoMediaItem> = props.mediaAlbum.mediae.map(
        (item) => item.obj
      );
      updatedMediaItems = updatedMediaItems.concat(mediaItems);
    }

    return updatedMediaItems;
  }, [props.mediaAlbum, props.keyPhoto]);

  const hasMultipleMediaItem: boolean = mediaItems.length > 1;

  const getImageSrcSet: (url: string) => string | null = (url: string) =>
    props.imageSizeGroup ? props.imageSizeGroup.getImageSrcset(url) : null;

  const getImageUrl: (url: string) => string = (url: string) => {
    if (props.size) return getResolutionImgUrl(url, props.size);

    return url;
  };

  const handleFocus = (isFocused: boolean) => {
    // enable keyboard navigation only when slider is focused
    // otherwise all of the sliders on the page will trigger on keyboard navigation;
    if (isFocused) {
      swiperInstance?.keyboard.enable();
    } else {
      swiperInstance?.keyboard.disable();
    }
  };

  const handleSlideChange = (
    e: React.MouseEvent<HTMLElement, MouseEvent> | undefined
  ) => {
    e?.stopPropagation();
    // record event only for menu items;
    props.itemId && recordEvent(eventOrigin, "view more images", props.itemId);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={cx(styles.media, wrapperClassName)}
      // add focus when there's > 1 slide
      onFocus={() => hasMultipleMediaItem && handleFocus(true)}
      onBlur={() => hasMultipleMediaItem && handleFocus(false)}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={hasMultipleMediaItem ? 0 : undefined}
    >
      {mediaItems?.length > 0 && (
        <>
          <Swiper
            centeredSlides={true}
            style={
              {
                "--swiper-navigation-color": "#fff",
                "--swiper-pagination-color": "#fff",
              } as React.CSSProperties
            }
            loop={hasMultipleMediaItem}
            onSwiper={setSwiperInstance}
            loopPreventsSliding={false}
            modules={[FreeMode, Navigation, Thumbs, Pagination, Keyboard]}
            enabled={hasMultipleMediaItem}
            pagination={hasMultipleMediaItem && !showThumbnails}
            simulateTouch={false}
            navigation={
              hasMultipleMediaItem
                ? {
                    prevEl: ".swiper-button-prev",
                    nextEl: ".swiper-button-next",
                  }
                : false
            }
            thumbs={
              hasMultipleMediaItem && showThumbnails
                ? {swiper: thumbsSwiper}
                : undefined
            }
          >
            {mediaItems.map((mediaItem) => (
              <SwiperSlide key={mediaItem.id}>
                <img
                  className={styles.swiperImage}
                  src={getImageUrl(mediaItem.url)}
                  alt=""
                  srcSet={getImageSrcSet(mediaItem.url) as string}
                  sizes={props.sizesAttribute}
                />
              </SwiperSlide>
            ))}
            {hasMultipleMediaItem && (
              <>
                <TransparentButton
                  className="swiper-button-prev"
                  onClick={handleSlideChange}
                >
                  <HideText>Previous image</HideText>
                </TransparentButton>
                <TransparentButton
                  className="swiper-button-next"
                  onClick={handleSlideChange}
                >
                  <HideText>Next image</HideText>
                </TransparentButton>
              </>
            )}
          </Swiper>
          {hasMultipleMediaItem && showThumbnails && (
            <Swiper
              onSwiper={setThumbsSwiper}
              slidesPerView={mediaItems.length}
              observeParents={true}
              // allows swiper to fire click event event if the mouse is dragged
              threshold={1000}
              modules={[Thumbs]}
              spaceBetween={10}
            >
              {mediaItems.map((mediaItem) => (
                <SwiperSlide key={mediaItem?.id}>
                  <div
                    data-testid={`thumbnail-${mediaItem?.id}`}
                    className={styles.mediaItemContainer}
                  >
                    <img src={mediaItem?.url} alt="" />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </>
      )}
    </div>
  );
}
